/** What a page needs of the dialogs' snapshot (/api/workspace: 2 MB, 3–5 s).
 *
 * - "none": the page's top bar is a label («Аналитика · время по Москве»), not the agent and scenario pickers. It opens
 *   on the bootstrap (actor, delivery, connectors, attention) and its own API; behind it the work context is read once —
 *   the snapshot's part without chats (employees, scenarios, stages, media: ~160 KB, ~50 ms) — for the menu's links and an
 *   instant move to «Диалоги»; no poll, no live stream. Until 18.10.2026 these pages read the whole snapshot behind them,
 *   again every minute and on every draft (r4 speed2: «Аналитика», «Эфиры», «Рассылки», «Лид-магниты» — 1.9 MB, 2–3 s of
 *   the server each).
 * - "context": the agent and scenario pickers of the top bar (app-shell's WorkContextBar) show the work context: read
 *   behind the page as above and kept fresh by the minute's poll. The default: a new page has the pickers unless the shell
 *   gives it a label.
 * - "dialogs-part": «Диалоги» read their part — that context before the page shows, then the chats on screen.
 * - "whole": the pages that show the whole snapshot from their first line wait for it.
 *
 * Until 18.10.2026 it was a hand-kept list of pages that open *without* the snapshot, and each new screen opened in 4–5 s
 * until someone added it (Ден 18.10: «сделать так, чтобы эта проблема не возникала при добавлении новых экранов»).
 * A page that reads the snapshot's lists or the work context built from them goes to "whole"; page-snapshot.test.ts finds
 * such pages in the code and checks that the top bar has the pickers exactly where the context is read. */
export type PageSnapshotNeed = "none" | "context" | "dialogs-part" | "whole";

/** «Диалоги» read their part of the snapshot: the chats on screen (see readDialogsPart). The root and the old addresses
 * of the dialogs (/leads, /review, /conversations) lead on to them. */
const DIALOGS_PART = /^\/(?:(?:dialogs|leads|review|conversations)\/*)?$/u;

/** Pages that show the whole snapshot from their first line. */
export const WHOLE_SNAPSHOT_PAGES = [
  "/agents", // employees, their scenarios and channels
  "/scenarios", // workflowScenarios, scenarioActionCapabilities, availableChannelRoutes
  "/semantic-staging", // «Полигон»: workflowScenarios
  "/status", // the selected scenario of the work context
  "/action-log", // the active scenarios and the agent of the work context filter the log
] as const;

// The paths are letters, «-» and «/»: nothing to escape.
const WHOLE = new RegExp(`^(?:${WHOLE_SNAPSHOT_PAGES.join("|")})(?:/|$)`, "u");

/** The pages whose top bar is a label (app-shell: «Аналитика», «Эфиры и серии», «Рассылки», «Лид-магниты», the calendar,
 * «Тревоги», «Обещания», «Роботы», «Боты», «Где что лежит»), as the shell matches them. */
const LABELLED = /^\/(?:analytics|webinars|lead-magnets|campaigns|calendar)(?:\/|$)|^\/(?:alerts|promises|robots|bots|where)$/u;

export function pageSnapshotNeed(pathname: string): PageSnapshotNeed {
  if (DIALOGS_PART.test(pathname)) return "dialogs-part";
  if (WHOLE.test(pathname)) return "whole";
  if (LABELLED.test(pathname)) return "none";
  return "context";
}
